// Semantic equalizer — stackable A↔B axis sliders that refine the current
// search. Each pair embeds once (cached); a slider walks the query along the
// meaning-direction unit(vec(B) − vec(A)); multiple axes compose additively.
// All math is client-side: only the final composed vector hits the server.
import { useRef, useState } from "preact/hooks";
import { api } from "../../api";
import { applyAxes, axisDirection } from "./vecMath";

export const PRESET_AXES: [string, string][] = [
  ["detailed", "minimal"],
  ["warm", "cool"],
  ["dark", "bright"],
  ["calm", "dramatic"],
  ["vintage", "modern"],
  ["busy", "empty"],
  ["natural", "urban"],
  ["photo", "illustration"],
];

interface Axis {
  a: string;
  b: string;
  t: number;
}

interface SemanticMixerProps {
  baseVec: number[] | null;
  /** Run a search with the composed vector (parent applies current filters). */
  onTune: (vec: number[]) => Promise<void>;
  /** All sliders back to zero — restore the base search. */
  onClear: () => void;
}

const vecCache = new Map<string, number[]>();

export function SemanticMixer({ baseVec, onTune, onClear }: SemanticMixerProps) {
  const [axes, setAxes] = useState<Axis[]>([]);
  const [status, setStatus] = useState("");
  const inFlight = useRef(false);
  const queued = useRef<number[] | null>(null);

  async function ensureEmbedded(texts: string[]) {
    const missing = [...new Set(texts.map((t) => t.trim()).filter(Boolean))].filter(
      (t) => !vecCache.has(t)
    );
    if (missing.length === 0) return;
    setStatus("embedding axis words…");
    const res = await api.search.embedTexts({ texts: missing });
    missing.forEach((t, i) => vecCache.set(t, res.vecs[i]!));
    setStatus("");
  }

  function composedVec(list: Axis[]): number[] | null {
    const dirs = [];
    for (const ax of list) {
      const va = vecCache.get(ax.a.trim());
      const vb = vecCache.get(ax.b.trim());
      if (va && vb && ax.t !== 0) dirs.push({ dir: axisDirection(va, vb), t: ax.t });
    }
    return applyAxes(baseVec, dirs);
  }

  async function runTune(vec: number[]) {
    if (inFlight.current) {
      queued.current = vec; // keep only the freshest position while a query runs
      return;
    }
    inFlight.current = true;
    try {
      await onTune(vec);
    } catch (e) {
      setStatus(`⚠ ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      inFlight.current = false;
      const next = queued.current;
      queued.current = null;
      if (next) void runTune(next);
    }
  }

  function retune(list: Axis[]) {
    const vec = composedVec(list);
    if (!vec) {
      onClear();
      return;
    }
    void runTune(vec);
  }

  async function addAxis(a = "", b = "") {
    const next = [...axes, { a, b, t: 0 }];
    setAxes(next);
    if (a && b) await ensureEmbedded([a, b]);
  }

  function setAxis(i: number, patch: Partial<Axis>, run = false) {
    const next = axes.map((ax, j) => (j === i ? { ...ax, ...patch } : ax));
    setAxes(next);
    if (run) retune(next);
  }

  function removeAxis(i: number) {
    const next = axes.filter((_, j) => j !== i);
    setAxes(next);
    retune(next);
  }

  const anyActive = axes.some((ax) => ax.t !== 0);

  return (
    <div class="ls-mixer">
      <div class="ls-row">
        <span class="ls-meta">
          <b>Tune</b> — semantic axes over the current search
        </span>
        {PRESET_AXES.filter(([a, b]) => !axes.some((ax) => ax.a === a && ax.b === b)).map(
          ([a, b]) => (
            <span key={`${a}-${b}`} class="ls-chip" onClick={() => void addAxis(a, b)}>
              {a} ↔ {b}
            </span>
          )
        )}
        <span class="ls-chip" onClick={() => void addAxis()}>
          + custom axis
        </span>
        {anyActive && (
          <span
            class="ls-chip"
            onClick={() => {
              setAxes(axes.map((ax) => ({ ...ax, t: 0 })));
              onClear();
            }}
          >
            ✕ clear tune
          </span>
        )}
      </div>
      {axes.map((ax, i) => (
        <div key={i} class="ls-axis">
          <input
            type="text"
            value={ax.a}
            placeholder="from, e.g. detailed"
            onInput={(e) => setAxis(i, { a: (e.target as HTMLInputElement).value })}
            onBlur={() => void ensureEmbedded([ax.a, ax.b])}
          />
          <input
            type="range"
            min="-1"
            max="1"
            step="0.05"
            value={ax.t}
            onInput={(e) => setAxis(i, { t: Number((e.target as HTMLInputElement).value) }, true)}
          />
          <input
            type="text"
            value={ax.b}
            placeholder="to, e.g. minimal"
            onInput={(e) => setAxis(i, { b: (e.target as HTMLInputElement).value })}
            onBlur={() => void ensureEmbedded([ax.a, ax.b])}
          />
          <span class="ls-axis-t">{ax.t.toFixed(2)}</span>
          <span class="ls-chip" onClick={() => removeAxis(i)}>
            ×
          </span>
        </div>
      ))}
      {status && <p class="ls-meta">{status}</p>}
      {axes.length > 0 && !baseVec && (
        <p class="ls-meta">
          no base search yet — sliders explore each axis's pure direction; run a search first to
          refine it instead
        </p>
      )}
    </div>
  );
}
